import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { map } from 'underscore';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import {
  entityIs,
  propertyIs,
  andComp,
} from '@bingads-webui-campaign-react/form-error';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';

import { PANEL_KEYS } from './panel-keys';
import { ERROR_ENTITY, campaignLanguageStrings } from '../../constants';
import { LanguageImg } from '../../media';
import { SectionShimmer, withShimmer } from '../shimmers';
import { ValidatedLanguageTarget, LanguageTargetValidatorFactory } from '../validated-language-target';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));

const ServerValidatedLanguageTargetWithShimmer =
  withShimmer(
    withControlErrorHandling(withUnvalidatedControlErrors(
      ValidatedLanguageTarget,
      { filter: campaignLanguagesProp }
    )),
    SectionShimmer
  );

export const LanguageTargetPanel = (props) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const {
    CurrentLocale: locale,
    DynamicPermissions,
    Permissions,
  } = serverContext;

  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const {
    isLoading,
    expand,
    data = {},
    topicKey,
    ...rest
  } = props;

  return (
    <ValidatedPanel
      key={PANEL_KEYS.LANGUAGE_TARGET}
      title={<TextTitle
        titleKey={_TL_('Language')}
        topicKey={topicKey || 'pop_BA_PMAX_Targeting_Language'}
        details={
          data && map(
            data.Languages,
            lang => i18n.getString(campaignLanguageStrings[lang] || lang)
          ).toString()}
        icon={LanguageImg}
      />}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
        expand,
        expandKey: PANEL_KEYS.LANGUAGE_TARGET,
      }}
      {...rest}
    >
      <ServerValidatedLanguageTargetWithShimmer
        showShimmer={isLoading}
        locale={locale}
        permissions={permissions}
        validationProps={{
          field: 'Languages',
          validator: LanguageTargetValidatorFactory(i18n),
        }}
      />
    </ValidatedPanel>
  );
};

LanguageTargetPanel.propTypes = {
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  data: PropTypes.shape({}),
  topicKey: PropTypes.string,
};

LanguageTargetPanel.defaultProps = {
  isLoading: false,
  data: {},
  topicKey: null,
};
